<script setup lang="ts">
  import { Picture, PictureFormat, Spinner, SpinnerSize } from '@travelata-libs/ui-kit';
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import type { Product } from '@/entities/product';
  import type { SearchCriteria } from '@/entities/searchCriteria';
  import type { PromoPlacement } from '@/shared/di';
  import { A51_STUB_PICTURE_PATH } from '../config/a51';
  import { attachA51Creative, releaseA51Creative, useA51Creative } from '../model/a51Creative';
  import { getA51Placement } from '../model/a51Placement';
  import { A51BannerState } from '../model/types';

  interface A51BannerProps {
    product: Product;
    searchCriteria: SearchCriteria;
    cardPosition: number;
  }

  const props = defineProps<A51BannerProps>();

  const placement = computed<PromoPlacement | null>(() => getA51Placement(props.searchCriteria));

  const { state, creative } = useA51Creative();

  const containerHost = ref<HTMLElement | null>(null);

  let attachedHost: HTMLElement | null = null;

  onMounted(() => {
    if (placement.value && containerHost.value) {
      attachA51Creative(placement.value, containerHost.value);
      attachedHost = containerHost.value;
    }
  });

  onBeforeUnmount(() => {
    if (attachedHost) {
      releaseA51Creative(attachedHost);
    }
  });
</script>

<template>
  <div
    v-if="placement"
    :class="['a51-banner', `a51-banner_${state}`]"
    data-testid="a51-banner"
  >
    <div
      v-show="!creative"
      ref="containerHost"
      class="a51-banner__container-host"
      data-testid="a51-banner-container-host"
    />

    <a
      v-if="creative"
      class="a51-banner__creative"
      :href="creative.reference || '#'"
      :target="creative.target || '_blank'"
      rel="noopener noreferrer"
      data-testid="a51-banner-creative"
    >
      <img
        class="a51-banner__creative-image"
        :src="creative.imageUrl"
        :alt="creative.alt || ''"
        :width="creative.width"
        :height="creative.height"
      />
    </a>

    <Spinner
      v-if="state === A51BannerState.Loading"
      :size="SpinnerSize.M"
      data-testid="a51-banner-loader"
    />

    <Picture
      v-if="state === A51BannerState.Stub"
      class="a51-banner__stub"
      :path="A51_STUB_PICTURE_PATH"
      :extensions="[PictureFormat.WEBP]"
      :high-resolution="false"
      aria-hidden="true"
      data-testid="a51-banner-stub"
    />
  </div>
</template>

<style scoped lang="scss">
  $a51-banner-height: 140px;

  .a51-banner {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;

    &_loading,
    &_stub {
      min-height: $a51-banner-height;
    }

    &__container-host {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    &__creative {
      display: block;
      max-width: 100%;
    }

    &__creative-image {
      display: block;
      height: auto;
      max-width: 100%;
    }

    &__stub {
      height: $a51-banner-height;
      width: 100%;
    }
  }
</style>
